/**
 * GDPR Management Page
 * Process GDPR data requests (Access, Delete, Export, Update)
 */
import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Card,
  CardContent,
  CardHeader,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Stack,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import gdprAPI, {
  ResourceType,
  GDPRUpdateRequest,
} from '../../services/gdprAPI';

const GDPRManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [externalId, setExternalId] = useState('');
  const [resourceType, setResourceType] = useState<ResourceType>('member');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [updateDialog, setUpdateDialog] = useState(false);
  const [updateData, setUpdateData] = useState<GDPRUpdateRequest>({
    person: {
      emailAddress: '',
      mobileNumber: '',
    },
  });

  const handleAccess = async () => {
    if (!externalId.trim()) {
      enqueueSnackbar('Molimo unesite Eksterni ID', { variant: 'warning' });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await gdprAPI.accessData(externalId, resourceType);
      setResult(response);
      enqueueSnackbar('Podaci uspešno preuzeti', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.detail || 'Neuspelo preuzimanje podataka',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'csv' | 'json' = 'csv') => {
    if (!externalId.trim()) {
      enqueueSnackbar('Molimo unesite Eksterni ID', { variant: 'warning' });
      return;
    }

    setLoading(true);

    try {
      const response = await gdprAPI.exportData(externalId, resourceType, format);

      if (format === 'csv') {
        gdprAPI.downloadCSV(
          response as Blob,
          `${resourceType}_${externalId}_export.csv`
        );
        enqueueSnackbar('CSV fajl preuzet', { variant: 'success' });
      } else {
        setResult(response);
        enqueueSnackbar('Podaci uspešno exportovani', { variant: 'success' });
      }
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.detail || 'Neuspelo exportovanje podataka',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    setDeleteDialog(false);

    try {
      const response = await gdprAPI.deleteData(externalId, resourceType);
      setResult(response);
      enqueueSnackbar('Podaci uspešno obrisani', { variant: 'success' });
      setExternalId(''); // Clear input after deletion
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.detail || 'Neuspelo brisanje podataka',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubmit = async () => {
    setLoading(true);
    setUpdateDialog(false);

    try {
      const response = await gdprAPI.updateData(
        externalId,
        resourceType,
        updateData
      );
      setResult(response);
      enqueueSnackbar('Podaci uspešno ažurirani', { variant: 'success' });
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.detail || 'Neuspelo ažuriranje podataka',
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SecurityIcon fontSize="large" />
          GDPR Zahtevi za Podatke
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Obrada GDPR prava: Pristup, Brisanje, Export i Ažuriranje podataka krajnjih korisnika
        </Typography>
      </Box>

      {/* Main Form Card */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Obrada Zahteva za Podatke"
          subheader="Unesite Eksterni ID (email ili jedinstveni identifikator) za obradu GDPR zahteva"
        />
        <CardContent>
          <Stack spacing={3}>
            {/* External ID Input */}
            <TextField
              fullWidth
              label="Eksterni ID"
              placeholder="john.doe@example.com"
              value={externalId}
              onChange={(e) => setExternalId(e.target.value)}
              helperText="Email adresa ili jedinstveni identifikator dodeljen članu/kuponu"
              disabled={loading}
            />

            {/* Resource Type Selection */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Tip Resursa
              </Typography>
              <RadioGroup
                row
                value={resourceType}
                onChange={(e) => setResourceType(e.target.value as ResourceType)}
              >
                <FormControlLabel
                  value="member"
                  control={<Radio />}
                  label="Član (Članska Kartica)"
                  disabled={loading}
                />
                <FormControlLabel
                  value="coupon"
                  control={<Radio />}
                  label="Kupon"
                  disabled={loading}
                />
                <FormControlLabel
                  value="ticket"
                  control={<Radio />}
                  label="Tiket (Event Ticket)"
                  disabled={loading}
                />
              </RadioGroup>
            </Box>

            <Divider />

            {/* Action Buttons */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                GDPR Akcije
              </Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={handleAccess}
                  disabled={loading || !externalId.trim()}
                >
                  Pristup Podacima
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('csv')}
                  disabled={loading || !externalId.trim()}
                >
                  Export CSV
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleExport('json')}
                  disabled={loading || !externalId.trim()}
                >
                  Export JSON
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<EditIcon />}
                  onClick={() => setUpdateDialog(true)}
                  disabled={loading || !externalId.trim()}
                >
                  Ažuriraj Podatke
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => setDeleteDialog(true)}
                  disabled={loading || !externalId.trim()}
                >
                  Obriši Podatke
                </Button>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* GDPR Info */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Implementirana GDPR Prava:</strong>
        </Typography>
        <ul style={{ marginTop: 8, paddingLeft: 20 }}>
          <li>Član 15: Pravo na Pristup (Pregled podataka)</li>
          <li>Član 16: Pravo na Ispravku (Ažuriranje podataka)</li>
          <li>Član 17: Pravo na Brisanje (Brisanje podataka)</li>
          <li>Član 20: Pravo na Prenosivost (Export podataka)</li>
        </ul>
      </Alert>

      {/* Loading Indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Results Display */}
      {result && !loading && (
        <Card>
          <CardHeader
            title="Rezultat Zahteva"
            action={
              <Chip
                label={result.message ? 'Uspešno' : 'Podaci Preuzeti'}
                color="success"
                size="small"
              />
            }
          />
          <CardContent>
            <Box
              sx={{
                backgroundColor: '#f5f5f5',
                p: 2,
                borderRadius: 1,
                maxHeight: '500px',
                overflow: 'auto',
              }}
            >
              <pre style={{ margin: 0, fontSize: '12px' }}>
                {JSON.stringify(result, null, 2)}
              </pre>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Potvrda Brisanja Podataka</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Ova akcija se ne može poništiti!
          </Alert>
          <Typography>
            Da li ste sigurni da želite da obrišete sve podatke za{' '}
            <strong>{externalId}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Ovo će:
          </Typography>
          <ul style={{ marginTop: 8 }}>
            <li>Obrisati podatke iz PassKit sistema</li>
            <li>Ukloniti pasoš iz Apple/Google Wallet-a</li>
            <li>Zapisati akciju u audit logove</li>
          </ul>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Otkaži</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Obriši Trajno
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Data Dialog */}
      <Dialog
        open={updateDialog}
        onClose={() => setUpdateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Ažuriraj Podatke</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Email Adresa"
              value={updateData.person?.emailAddress || ''}
              onChange={(e) =>
                setUpdateData({
                  ...updateData,
                  person: {
                    ...updateData.person,
                    emailAddress: e.target.value,
                  },
                })
              }
              helperText="Ostavite prazno da zadržite trenutnu vrednost"
            />
            <TextField
              fullWidth
              label="Broj Mobilnog"
              value={updateData.person?.mobileNumber || ''}
              onChange={(e) =>
                setUpdateData({
                  ...updateData,
                  person: {
                    ...updateData.person,
                    mobileNumber: e.target.value,
                  },
                })
              }
              helperText="Format: +381641234567"
            />
            <Alert severity="info">
              Izmene će biti automatski poslate na Apple/Google Wallet
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateDialog(false)}>Otkaži</Button>
          <Button
            onClick={handleUpdateSubmit}
            color="primary"
            variant="contained"
          >
            Ažuriraj Podatke
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GDPRManagement;
